import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import koraIcon from '@/assets/imgs/kora-icon.svg';

export default function Login() {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await login({ email, senha });
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error || 'Erro ao fazer login');
    }
    
    setIsLoading(false);
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#161616] p-4">
      {/* brilho dourado de fundo, sem depender de foto externa */}
      <div
        className="pointer-events-none absolute -top-1/4 left-1/2 h-[50rem] w-[50rem] -translate-x-1/2 rounded-full opacity-40 blur-3xl"
        style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.5), transparent 70%)' }}
      />
      <div
        className="pointer-events-none absolute bottom-0 right-0 h-[36rem] w-[36rem] translate-x-1/4 translate-y-1/4 rounded-full opacity-30 blur-3xl"
        style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.6), transparent 70%)' }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      <div className="relative z-10 grid w-full max-w-6xl gap-8 md:grid-cols-2 md:items-center">
        <div className="hidden space-y-6 text-white md:block">
          <div className="flex items-center gap-3">
            <img src={koraIcon} alt="kora" className="h-14 w-14 rounded-2xl object-contain shadow-lg" />
            <div className="leading-tight">
              <p className="font-display text-2xl font-bold">kora</p>
              <p className="text-xs uppercase tracking-widest text-white/60">gestão estratégica</p>
            </div>
          </div>
          <h2 className="font-display text-4xl font-bold leading-tight lg:text-5xl">
            Planeje metas, acompanhe resultados e mantenha foco no que importa.
          </h2>
          <p className="max-w-md text-sm text-white/70 lg:text-base">
            Centralize objetivos da empresa, melhore a governança de indicadores e acelere a execução com visibilidade em tempo real.
          </p>
        </div>

        <Card className="w-full border-border/60 bg-card/95 shadow-2xl backdrop-blur-md md:ml-auto md:max-w-md">
          <CardHeader className="space-y-3 pb-2">
            <div className="mx-auto rounded-2xl bg-card p-3 shadow-md ring-1 ring-border">
              <img src={koraIcon} alt="kora" className="h-16 w-16 object-contain sm:h-20 sm:w-20" />
            </div>
            <div className="space-y-0.5 text-center">
              <p className="font-display text-xl font-bold text-foreground">kora</p>
              <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
                gestão estratégica
              </p>
              <CardDescription className="pt-2 text-sm text-muted-foreground">
                Acesse seu ambiente de gestão de objetivos
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" className="animate-fade-in">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="senha">Senha</Label>
                <PasswordInput
                  id="senha"
                  placeholder="••••••"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  required
                  className="h-11"
                />
              </div>

              <div className="text-right">
                <Link to="/esqueci-senha" className="text-sm text-primary hover:underline">
                  Esqueci minha senha
                </Link>
              </div>

              <Button
                type="submit"
                className="h-11 w-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Entrando...
                  </>
                ) : (
                  'Entrar'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
